import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import map from 'lodash/map';
import { ArrowLeftIcon, ArrowRightIcon } from '@/icons';
import { cn } from '@/utils/mergeClassName';
import { paginationButtonVariants, textVariants } from './styles';

export type PaginationProps = {
  totalItems: number;
  itemsPerPage?: number;
  currentPage?: number;
  variant?: 'text' | 'arrow';
  disabled?: boolean;
  className?: string;
  scroll?: boolean;
};

const siblingsCount = 2;

function generatePagesArray(from: number, to: number) {
  return [...new Array(to - from)]
    .map((_page, index) => from + index + 1)
    .filter((page) => page > 0);
}

export const Pagination = ({
  totalItems,
  itemsPerPage = 10,
  currentPage = 1,
  variant = 'text',
  disabled = false,
  className,
  scroll = false
}: PaginationProps) => {
  const searchParams = useSearchParams();
  const currentParams = searchParams.size > 0 ? Object.fromEntries(searchParams) : {};

  const lastPage = Math.ceil(totalItems / itemsPerPage);

  const previousPages =
    currentPage > 1 ? generatePagesArray(currentPage - 1 - siblingsCount, currentPage - 1) : [];

  const nextPages =
    currentPage < lastPage
      ? generatePagesArray(currentPage, Math.min(currentPage + siblingsCount, lastPage))
      : [];

  return (
    <nav
      aria-label="pagination"
      className={cn('flex items-center justify-center gap-8', className)}
    >
      <ul className="flex flex-row items-center justify-center">
        <li>
          <Link
            aria-disabled={currentPage === 1 || disabled}
            className={paginationButtonVariants({ class: 'mr-8' })}
            href={{
              query: {
                ...currentParams,
                pagina: currentPage - 1
              }
            }}
            scroll={scroll}
          >
            {variant === 'text' ? (
              'Anterior'
            ) : (
              <>
                <ArrowLeftIcon />
                <span className="sr-only">Página anterior</span>
              </>
            )}
          </Link>
        </li>
        {currentPage > 1 + siblingsCount && (
          <>
            <li>
              <Link
                data-state="inactive"
                aria-disabled={disabled}
                className={paginationButtonVariants()}
                href={{
                  query: {
                    ...currentParams,
                    pagina: 1
                  }
                }}
                scroll={scroll}
              >
                <span className="sr-only">Página</span> 1
              </Link>
            </li>
            {currentPage > 2 + siblingsCount && (
              <li>
                <span className={textVariants({ class: 'select-none text-ellipsis' })}>
                  &#8230;
                </span>
              </li>
            )}
          </>
        )}

        {previousPages.length > 0 &&
          map(previousPages, (page) => (
            <li key={page}>
              <Link
                data-state="inactive"
                aria-disabled={disabled}
                className={paginationButtonVariants()}
                href={{
                  query: {
                    ...currentParams,
                    pagina: page
                  }
                }}
                scroll={scroll}
              >
                <span className="sr-only">Página</span> {page}
              </Link>
            </li>
          ))}

        <li>
          <Link
            data-state="active"
            aria-disabled={disabled}
            className={paginationButtonVariants()}
            href={{
              query: {
                ...currentParams,
                pagina: currentPage
              }
            }}
            scroll={scroll}
          >
            <span className="sr-only">Página</span> {currentPage}
          </Link>
        </li>

        {nextPages.length > 0 &&
          map(nextPages, (page) => (
            <li key={page}>
              <Link
                data-state="inactive"
                aria-disabled={disabled}
                className={paginationButtonVariants()}
                href={{
                  query: {
                    ...currentParams,
                    pagina: page
                  }
                }}
                scroll={scroll}
              >
                <span className="sr-only">Página</span> {page}
              </Link>
            </li>
          ))}

        {currentPage + siblingsCount < lastPage && (
          <>
            {currentPage + 1 + siblingsCount < lastPage && (
              <li>
                <span className={textVariants({ class: 'text-ellipsis' })}>&#8230;</span>
              </li>
            )}
            <li>
              <Link
                data-state="inactive"
                aria-disabled={disabled}
                className={paginationButtonVariants()}
                href={{
                  query: {
                    ...currentParams,
                    pagina: lastPage
                  }
                }}
                scroll={scroll}
              >
                <span className="sr-only">Página</span> {lastPage}
              </Link>
            </li>
          </>
        )}
        <li>
          <Link
            aria-disabled={currentPage === lastPage || disabled}
            tabIndex={currentPage === lastPage || disabled ? -1 : undefined}
            className={paginationButtonVariants({ class: 'ml-8' })}
            href={{
              query: {
                ...currentParams,
                pagina: currentPage + 1
              }
            }}
            scroll={scroll}
          >
            {variant === 'text' ? (
              'Próximo'
            ) : (
              <>
                <ArrowRightIcon />
                <span className="sr-only">Próxima página</span>
              </>
            )}
          </Link>
        </li>
      </ul>
    </nav>
  );
};
